import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { BookOpen, Scale, HelpCircle, ClipboardCheck, FileStack, MessagesSquare, ArrowRight, Inbox } from "lucide-react";

const STATS = [
  { key: "review_queue", label: "Review Queue", icon: Inbox, to: "/admin/review", accent: "text-rose-700" },
  { key: "canonical", label: "Canonical Knowledge", icon: BookOpen, to: "/admin/constitution", accent: "text-emerald-700" },
  { key: "working", label: "Working Items", icon: Scale, to: "/admin/constitution", accent: "text-amber-700" },
  { key: "open_questions", label: "Open Questions", icon: HelpCircle, to: "/admin/open-questions", accent: "text-blue-700" },
  { key: "unreviewed_claims", label: "Unreviewed Claims", icon: ClipboardCheck, to: "/admin/claims", accent: "text-red-700" },
  { key: "sources", label: "Sources Imported", icon: FileStack, to: "/admin/sources", accent: "text-slate-700" },
  { key: "conversations", label: "Conversations Imported", icon: MessagesSquare, to: "/admin/conversations", accent: "text-slate-700" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader title="Institutional Memory" subtitle="The authoritative, founder-approved state of Pramaana — knowledge, decisions, unresolved questions, and evidence." />
      <div className="p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="dashboard-stats">
          {STATS.map(({ key, label, icon: Icon, to, accent }) => (
            <Link
              key={key}
              to={to}
              data-testid={`stat-${key}`}
              className="group bg-white border border-slate-200 p-6 hover:border-slate-400 transition-colors duration-150"
            >
              <div className="flex items-start justify-between">
                <Icon size={20} strokeWidth={1.5} className={accent} />
                <ArrowRight size={16} className="text-slate-300 group-hover:text-slate-600 transition-colors" />
              </div>
              <div className="font-serif text-5xl text-slate-900 mt-4 leading-none">
                {data ? (data[key] ?? 0) : "—"}
              </div>
              <div className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mt-3">{label}</div>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-4">Recent Founder Corrections</h3>
            {data?.recent_corrections?.length ? (
              <ul className="space-y-3">
                {data.recent_corrections.map((c) => (
                  <li key={c.id} className="text-sm text-slate-700 border-l-2 border-slate-900 pl-3">
                    <span className="font-mono text-xs text-slate-500">{c.field}</span> — {c.reason}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-400">No corrections recorded yet. Constitutional history is preserved immutably.</p>
            )}
          </div>
          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-4">Recent Knowledge Changes</h3>
            {data?.recent_changes?.length ? (
              <ul className="space-y-3">
                {data.recent_changes.map((c) => (
                  <li key={c.id} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mt-0.5 shrink-0">{c.change_type?.replace(/_/g, " ")}</span>
                    <span className="truncate">{c.reason}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-400">No changes yet.</p>
            )}
          </div>
        </div>

        <div className="mt-12 border border-slate-200 bg-slate-900 text-slate-100 p-8">
          <p className="font-serif text-2xl leading-snug max-w-3xl">
            Pramaana Founder Intelligence preserves not only information, but authority, attribution, decisions, uncertainty, provenance, and the evolution of founder thinking.
          </p>
        </div>
      </div>
    </div>
  );
}
